import { z } from "zod";
import { isValidSubPurpose, ToolCategorySchema } from './model-sub-purpose.schema';

/**
 * AI Model Schema v2
 * @package @genieartinc/schemas
 */

// Node Types
export const AI_NODE_TYPES = {
  IMAGE: "image",
  VIDEO: "video",
  SOUND: "sound",
  THREE_D: "3d",
  TEXT_LLM: "text-llm",
} as const;

export type AiNodeType = (typeof AI_NODE_TYPES)[keyof typeof AI_NODE_TYPES];

// Node Type Zod enum (single source of truth)
const AiNodeTypeValues = ["image", "video", "sound", "3d", "text-llm"] as const;
export const AiNodeTypeSchema = z.enum(AiNodeTypeValues);

// Model Status
export const AI_MODEL_STATUS = {
  ACTIVE: "active",
  BETA: "beta",
  DEPRECATED: "deprecated",
  EXPERIMENTAL: "experimental",
  REMOVED: "removed",
} as const;

export type AiModelStatus = (typeof AI_MODEL_STATUS)[keyof typeof AI_MODEL_STATUS];

// Model Purpose
export const AI_MODEL_PURPOSE = {
  CREATE: "create", // Text-to-image, text-to-video
  EDIT: "edit", // Image-to-image, style transfer
  ENHANCE: "enhance", // Upscaling, enhancement, refinement
  TOOLS: "tools", // Utility models: preprocessors, removers, OCR, transcription, training
} as const;

export type AiModelPurpose = (typeof AI_MODEL_PURPOSE)[keyof typeof AI_MODEL_PURPOSE];

// Zod schema for Model Purpose (DRY - single source of truth)
export const AiModelPurposeSchema = z.enum(["create", "edit", "enhance", "tools"]);

// Human-readable labels for purposes
export const AI_MODEL_PURPOSE_LABELS: Record<AiModelPurpose, string> = {
  create: "Create",
  edit: "Edit",
  enhance: "Enhance",
  tools: "Tools",
};

/**
 * Helper function to get purpose label
 */
export function getPurposeLabel(purpose: string): string {
  if (AiModelPurposeSchema.safeParse(purpose).success) {
    return AI_MODEL_PURPOSE_LABELS[purpose as AiModelPurpose];
  }
  return purpose;
}

// Pricing Source
export const PricingSourceSchema = z.enum(['api', 'scraper', 'manual', 'estimated']).default('api');
export type PricingSource = z.infer<typeof PricingSourceSchema>;

// Pricing Schema (single source of truth for both DB storage and sync tool)
export const AiModelPricingSchema = z.object({
  /** Original price from provider API */
  originalPrice: z.number().optional(),
  /** Fallback/estimated price if original not available */
  fallbackPrice: z.number().optional(),
  /** Currency code (always USD after normalization) */
  currency: z.string().default("USD"),
  /** Pricing unit (e.g., 'per_image', 'per_second', 'per_token') */
  unit: z.string().optional(),
  /** ISO date string when price was last updated */
  priceLastUpdated: z.string().optional(),
  /** Source of pricing information */
  pricingSource: PricingSourceSchema,
});

export type AiModelPricing = z.infer<typeof AiModelPricingSchema>;

// Base AI Model Schema (without refinements — used for .omit()/.pick())
export const AiModelBaseSchema = z.object({
  id: z.string(),
  endpointId: z.string(), // Unique endpoint identifier (replaces openApiEndpointId)
  name: z.string(),
  description: z.string().optional(),

  // Media & Metadata (from provider API)
  thumbnailUrl: z.string().optional(), // CDN cover image from provider
  thumbnailAnimatedUrl: z.string().optional(), // Animated thumbnail (optional)
  licenseType: z.string().optional(), // "commercial", "research", "private"
  modelKind: z.string().optional(), // "inference", "training"
  durationEstimate: z.number().optional(), // Estimated job duration in seconds

  // Provider & Category
  providerId: z.string(),
  providerCategory: z.string(), // "text-to-image", "image-to-video", etc. (renamed from category)
  nodeType: AiNodeTypeSchema, // REQUIRED

  // Family & Purpose
  modelFamilyId: z.string().optional(), // Renamed from brandId
  familyVersion: z.string().optional(), // e.g., "v1", "v2", "xl"
  purpose: AiModelPurposeSchema, // REQUIRED
  subPurpose: z.string().optional(), // Validated by refinement below
  toolCategory: ToolCategorySchema.optional(), // Derived from subPurpose — grouping for toolbar/library/admin

  // Quality & Classification
  qualityTier: z.string().optional(), // e.g., "premium", "standard", "budget"
  classificationMethod: z.string().default("llm"), // "llm", "rule-based", "manual"
  classificationConfidence: z.number().optional(), // 0-1 confidence score
  classifiedAt: z.date().optional(),
  classifiedBy: z.string().optional(), // "gpt-4o", "rule-engine", "admin:orkhan"

  // Status & Control
  status: z.enum(["active", "beta", "deprecated", "experimental", "removed"]).default("active"),
  enabled: z.boolean().default(true),
  isDefault: z.boolean().default(false),

  // Featured Score
  featuredScore: z.number().default(0),

  // Pricing (optional - Json?)
  pricing: AiModelPricingSchema.optional(),

  // Capabilities
  /** Derived from providerCategory + subPurpose + filtered fal.ai tags */
  capabilities: z.array(z.string()).default([]),

  // Parameter Schema Management
  parameterSchema: z.any().optional(), // JSON schema from OpenAPI
  parameterOverrides: z.any().optional(), // Admin overrides for UI customization
  schemaVersion: z.string().optional(), // Schema version for cache invalidation
  schemaFetchedAt: z.date().optional(), // Last time schema was fetched
  rawOpenApiSchema: z.any().optional(), // Raw OpenAPI schema from provider

  // Sync Tracking
  syncSource: z.string().default("api"),
  lastSyncedAt: z.date(),

  // Timestamps
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().optional(),
});

// Full AI Model Schema with subPurpose cross-validation
export const AiModelSchema = AiModelBaseSchema.refine(
  (data) => {
    if (data.subPurpose && data.nodeType && data.purpose) {
      return isValidSubPurpose(data.nodeType, data.purpose, data.subPurpose);
    }
    return true;
  },
  {
    message: "SubPurpose is not valid for the selected nodeType and purpose combination",
    path: ["subPurpose"],
  }
);

export type AiModel = z.infer<typeof AiModelSchema>;

// Create DTO (uses base schema to avoid Zod v4 .omit() refinement limitation)
export const CreateAiModelSchema = AiModelBaseSchema.omit({
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
}).extend({
  lastSyncedAt: z.date().default(() => new Date()),
});

export type CreateAiModel = z.infer<typeof CreateAiModelSchema>;

// Update DTO (for admin panel)
export const UpdateAiModelSchema = z.object({
  name: z.string().optional(),
  description: z.string().optional(),
  endpointId: z.string().optional(),
  nodeType: AiNodeTypeSchema.optional(),
  providerCategory: z.string().optional(),
  modelFamilyId: z.string().nullable().optional(),
  familyVersion: z.string().nullable().optional(),
  purpose: AiModelPurposeSchema.nullable().optional(),
  subPurpose: z.string().nullable().optional(),
  toolCategory: ToolCategorySchema.nullable().optional(),
  qualityTier: z.string().nullable().optional(),
  classificationMethod: z.string().optional(),
  classificationConfidence: z.number().nullable().optional(),
  classifiedAt: z.date().nullable().optional(),
  classifiedBy: z.string().nullable().optional(),
  status: z.enum(["active", "beta", "deprecated", "experimental", "removed"]).optional(),
  enabled: z.boolean().optional(),
  isDefault: z.boolean().optional(),
  featuredScore: z.number().optional(),
  parameterSchema: z.any().optional(), // JSON schema from OpenAPI
  parameterOverrides: z.any().optional(), // Admin overrides for UI customization
  schemaVersion: z.string().optional(), // Schema version for cache invalidation
  schemaFetchedAt: z.date().optional(), // Last time schema was fetched
  rawOpenApiSchema: z.any().optional(), // Raw OpenAPI schema from provider
  thumbnailUrl: z.string().optional(),
  thumbnailAnimatedUrl: z.string().optional(),
  licenseType: z.string().optional(),
  modelKind: z.string().optional(),
  durationEstimate: z.number().optional(),
});

export type UpdateAiModel = z.infer<typeof UpdateAiModelSchema>;

// Filter DTO
export const AiModelFiltersSchema = z.object({
  providerCategory: z.string().optional(), // Renamed from category
  nodeType: AiNodeTypeSchema.optional(),
  modelFamilyId: z.string().optional(), // Renamed from brandId
  modelFamily: z.string().optional(), // Renamed from brand - filter by family name
  purpose: AiModelPurposeSchema.optional(),
  purposeNot: AiModelPurposeSchema.optional(),
  subPurpose: z.string().optional(),
  toolCategory: ToolCategorySchema.optional(),
  enabled: z.boolean().optional(),
  status: z.enum(["active", "beta", "deprecated", "experimental", "removed"]).optional(),
  providerId: z.string().optional(),
  search: z.string().optional(), // Search in name/description
  page: z.number().default(1),
  limit: z.number().default(50),
});

export type AiModelFilters = z.infer<typeof AiModelFiltersSchema>;

// List Response DTO
export const AiModelListResponseSchema = z.object({
  data: z.array(AiModelSchema),
  meta: z.object({
    page: z.number(),
    limit: z.number(),
    total: z.number(),
    totalPages: z.number(),
  }),
});

export type AiModelListResponse = z.infer<typeof AiModelListResponseSchema>;
